import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { readSupabaseEnv } from '../lib/env';
import { selectSessionStorage, type SessionStoragePort } from '../platform/session-storage';
import type { Database } from './database.types';

export type CrewlinkClient = SupabaseClient<Database>;

/**
 * The Supabase client, or null when no configuration is present.
 *
 * Null is a real state right now: the hosted Crewlink Supabase project does not
 * exist yet and only Jerry can create it. Returning null lets the app render a
 * truthful "not configured" screen instead of throwing at module load, which
 * would be a blank page with a console error.
 */
let cached: CrewlinkClient | null = null;
let attempted = false;

export function getSupabaseClient(): CrewlinkClient | null {
  if (attempted) return cached;
  attempted = true;

  const env = readSupabaseEnv();
  if (!env) {
    cached = null;
    return null;
  }

  cached = createSupabaseClient(env.url, env.anonKey, selectSessionStorage());
  return cached;
}

/**
 * Exported separately so tests can build a client against a local instance with
 * an explicit storage port, without touching the module-level singleton.
 *
 * Session material goes through the PORT, never straight to localStorage — the
 * seam the native secure-storage adapter is installed behind later (D-005,
 * INV-T24). See `src/platform/session-storage.ts`.
 */
export function createSupabaseClient(
  url: string,
  anonKey: string,
  storage: SessionStoragePort,
): CrewlinkClient {
  return createClient<Database>(url, anonKey, {
    auth: {
      storage,
      // Persist across restarts — Brandon does not sign in every time (D-005).
      persistSession: true,
      autoRefreshToken: true,
      // MUST stay true. Password recovery depends on it.
      //
      // This was previously false, reasoned as "v1 has no OAuth and no magic
      // links, so an app URL never carries an auth fragment" (D-005). That
      // reasoning was wrong: Supabase's password-recovery email redirects back
      // with an IMPLICIT-FLOW FRAGMENT — `#access_token=…&refresh_token=…&
      // type=recovery`. With detection off, supabase-js never consumed that
      // fragment, no session was established, and no PASSWORD_RECOVERY event
      // fired, so every valid reset link was reported as invalid.
      //
      // Leaving it on also removes the tokens from the address bar: the client
      // strips the fragment via history.replaceState once consumed, so they do
      // not linger in the URL or in browser history.
      detectSessionInUrl: true,
    },
  });
}

/** True when the app has been given somewhere to talk to. */
export function isSupabaseConfigured(): boolean {
  return getSupabaseClient() !== null;
}
